'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import type { Property } from '@/lib/api/clients/properties';
import { StoneInvestmentPropertyCard } from './PropertyCard';
import { 
  getRegionColors, 
  getRegionFlag, 
  regionNames,
} from '../utils/regionColors';

export interface RegionPageProps {
  region: 'provence' | 'mauritius';
  properties: Property[];
  locale?: 'en' | 'fr';
  messages?: {
    title?: string;
    subtitle?: string;
    description?: string;
    viewAllProperties?: string;
    featuredProperties?: string;
    aboutTheRegion?: string;
    investmentOpportunities?: string;
    contactUs?: string;
    interestedIn?: string;
    ctaDescription?: string;
  };
}

/**
 * Region-specific content for each destination
 */
const regionContent = {
  provence: {
    en: {
      heroTitle: 'Provence',
      heroSubtitle: 'The Art of Living in the South of France',
      description: 'Discover exceptional properties in the heart of Provence, where centuries-old bastides, elegant mas, and contemporary villas await discerning buyers seeking the quintessential Mediterranean lifestyle.',
      keyPoints: [
        { title: 'Luberon & Alpilles', desc: 'Iconic hill villages and vineyard estates' },
        { title: 'Côte d\'Azur', desc: 'Prestigious waterfront properties' },
        { title: 'Aix-en-Provence', desc: 'Cultural heritage and urban elegance' },
        { title: 'Saint-Rémy', desc: 'Charming market towns and countryside' },
      ],
      lifestyle: 'From lavender fields to olive groves, Provence offers an unparalleled quality of life that has attracted artists, writers, and connoisseurs for centuries.',
    },
    fr: {
      heroTitle: 'Provence',
      heroSubtitle: 'L\'Art de Vivre dans le Sud de la France',
      description: 'Découvrez des propriétés exceptionnelles au cœur de la Provence, où bastides séculaires, mas élégants et villas contemporaines attendent les acheteurs avertis en quête du style de vie méditerranéen par excellence.',
      keyPoints: [
        { title: 'Luberon & Alpilles', desc: 'Villages perchés emblématiques et domaines viticoles' },
        { title: 'Côte d\'Azur', desc: 'Propriétés prestigieuses en bord de mer' },
        { title: 'Aix-en-Provence', desc: 'Patrimoine culturel et élégance urbaine' },
        { title: 'Saint-Rémy', desc: 'Charmants villages de marché et campagne' },
      ],
      lifestyle: 'Des champs de lavande aux oliveraies, la Provence offre une qualité de vie inégalée qui attire artistes, écrivains et connaisseurs depuis des siècles.',
    },
    heroImage: '/images/regions/provence-hero.jpg',
    galleryImages: [
      '/images/regions/provence-1.jpg',
      '/images/regions/provence-2.jpg',
      '/images/regions/provence-3.jpg',
    ],
  },
  mauritius: {
    en: {
      heroTitle: 'Mauritius',
      heroSubtitle: 'Tropical Paradise in the Indian Ocean',
      description: 'Explore exclusive properties on this pristine island paradise, where world-class golf resorts, beachfront villas, and investment opportunities under the PDS and RES schemes offer exceptional returns and lifestyle.',
      keyPoints: [
        { title: 'Grand Baie', desc: 'Vibrant coastal living and marina lifestyle' },
        { title: 'Black River', desc: 'Prestigious golf and beach estates' },
        { title: 'Bel Ombre', desc: 'Exclusive heritage resorts' },
        { title: 'East Coast', desc: 'Pristine lagoons and coral reefs' },
      ],
      lifestyle: 'With year-round tropical weather, world-class infrastructure, and attractive tax incentives, Mauritius represents the pinnacle of island living.',
    },
    fr: {
      heroTitle: 'Île Maurice',
      heroSubtitle: 'Paradis Tropical dans l\'Océan Indien',
      description: 'Explorez des propriétés exclusives sur cette île paradisiaque, où complexes de golf de classe mondiale, villas en bord de mer et opportunités d\'investissement sous les régimes PDS et RES offrent des rendements exceptionnels.',
      keyPoints: [
        { title: 'Grand Baie', desc: 'Vie côtière dynamique et marina' },
        { title: 'Rivière Noire', desc: 'Domaines de golf et plage prestigieux' },
        { title: 'Bel Ombre', desc: 'Complexes patrimoniaux exclusifs' },
        { title: 'Côte Est', desc: 'Lagons immaculés et récifs coralliens' },
      ],
      lifestyle: 'Avec un climat tropical toute l\'année, des infrastructures de classe mondiale et des incitations fiscales attractives, Maurice représente le summum de la vie insulaire.',
    },
    heroImage: '/images/regions/mauritius-hero.jpg',
    galleryImages: [
      '/images/regions/mauritius-1.jpg',
      '/images/regions/mauritius-2.jpg',
      '/images/regions/mauritius-3.jpg',
    ],
  },
};

/**
 * Stone Investment Region Page
 * 
 * Dedicated page template for Provence and Mauritius properties,
 * featuring region-specific branding and content.
 */
export function RegionPage({
  region,
  properties,
  locale = 'en',
  messages,
}: RegionPageProps) {
  const colors = getRegionColors(region);
  const flag = getRegionFlag(region);
  const content = regionContent[region][locale];
  const regionName = regionNames[region][locale];

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      {/* Hero Section with Region-specific Accent */}
      <section className="relative min-h-[80vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          <Image
            src={regionContent[region].heroImage}
            alt={content.heroTitle}
            fill
            className="object-cover grayscale-[20%]"
            priority
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        </div>

        {/* Region Accent Bar - Top */}
        <div 
          className="absolute top-0 left-0 right-0 h-1"
          style={{ background: colors.gradient }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Region Badge */}
            <div className="flex items-center gap-3 mb-8">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: colors.primary }}
              />
              <span 
                className="text-[11px] tracking-[0.3em] uppercase"
                style={{ 
                  fontFamily: "'DM Sans', sans-serif",
                  color: colors.secondary,
                }}
              >
                {flag} {regionName}
              </span>
            </div>

            <h1 
              className="text-5xl sm:text-6xl lg:text-8xl text-white mb-6"
              style={{ 
                fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                fontWeight: 300,
                letterSpacing: '-0.02em',
              }}
            >
              {messages?.title || content.heroTitle}
            </h1>

            <p 
              className="text-lg sm:text-xl lg:text-2xl text-white/70 max-w-2xl mb-8"
              style={{ 
                fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                fontWeight: 300,
                fontStyle: 'italic',
              }}
            >
              {messages?.subtitle || content.heroSubtitle}
            </p>

            <p 
              className="text-sm text-white/60 max-w-xl leading-relaxed"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {messages?.description || content.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Areas Section */}
      <section className="py-24 lg:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {content.keyPoints.map((point, index) => (
              <div 
                key={point.title}
                className="relative p-6 border border-white/10 group hover:border-white/20 transition-colors"
                style={{ 
                  backgroundColor: colors.background,
                }}
              >
                {/* Accent bar */}
                <div 
                  className="absolute top-0 left-0 bottom-0 w-0.5"
                  style={{ backgroundColor: colors.primary + '60' }}
                />
                
                <span 
                  className="text-[10px] tracking-[0.2em] uppercase mb-2 block"
                  style={{ 
                    fontFamily: "'DM Sans', sans-serif",
                    color: colors.primary,
                  }}
                >
                  0{index + 1}
                </span>
                
                <h3 
                  className="text-xl text-white mb-2"
                  style={{ 
                    fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                    fontWeight: 400,
                  }}
                >
                  {point.title}
                </h3>
                
                <p 
                  className="text-sm text-white/50"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {point.desc}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Lifestyle Quote */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div 
              className="inline-block w-16 h-0.5 mb-8"
              style={{ backgroundColor: colors.primary }}
            />
            
            <p 
              className="text-2xl lg:text-3xl text-white/80 leading-relaxed"
              style={{ 
                fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                fontWeight: 300,
                fontStyle: 'italic',
              }}
            >
              &ldquo;{content.lifestyle}&rdquo;
            </p>
            
            <div 
              className="inline-block w-16 h-0.5 mt-8"
              style={{ backgroundColor: colors.primary }}
            />
          </motion.div>
        </div>
      </section>

      {/* Properties Section */}
      <section className="py-24 lg:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12"
          >
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-lg">{flag}</span>
                <span 
                  className="text-[10px] tracking-[0.25em] uppercase"
                  style={{ 
                    fontFamily: "'DM Sans', sans-serif",
                    color: colors.primary,
                  }}
                >
                  {messages?.featuredProperties || 'Featured Properties'}
                </span>
              </div>
              
              <h2 
                className="text-3xl lg:text-4xl text-white"
                style={{ 
                  fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                  fontWeight: 400,
                }}
              >
                {region === 'provence' ? 'Properties in Provence' : 'Properties in Mauritius'}
              </h2>
            </div>

            <Link 
              href={`/${locale}/properties?region=${region}`}
              className="inline-flex items-center gap-2 text-sm group"
              style={{ 
                fontFamily: "'DM Sans', sans-serif",
                color: colors.primary,
              }}
            >
              <span className="tracking-[0.15em] uppercase">
                {messages?.viewAllProperties || 'View All Properties'}
              </span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>

          {/* Properties Grid */}
          {properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <StoneInvestmentPropertyCard
                    property={property}
                    variant="portfolio"
                    locale={locale}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/10">
              <p 
                className="text-white/50"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                No properties currently available. Contact us for upcoming opportunities.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section 
        className="py-24 lg:py-32 border-t"
        style={{ borderColor: colors.primary + '30' }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span 
              className="text-[10px] tracking-[0.3em] uppercase mb-6 block"
              style={{ 
                fontFamily: "'DM Sans', sans-serif",
                color: colors.primary,
              }}
            >
              {flag} {messages?.investmentOpportunities || 'Investment Opportunities'}
            </span>

            <h2 
              className="text-3xl lg:text-4xl text-white mb-8"
              style={{ 
                fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                fontWeight: 400,
              }}
            >
              {messages?.interestedIn
                ? messages.interestedIn.replace('{{region}}', regionNames[region][locale])
                : `Interested in ${region === 'provence' ? 'Provence' : 'Mauritius'}?`}
            </h2>

            <p 
              className="text-white/60 mb-10 max-w-xl mx-auto"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {messages?.ctaDescription || (locale === 'fr'
                ? 'Notre équipe est spécialisée dans la mise en relation d\'acheteurs exigeants avec des propriétés d\'exception. Contactez-nous pour un accompagnement personnalisé et des biens exclusifs.'
                : 'Our team specializes in connecting discerning buyers with exceptional properties. Contact us for personalized guidance and exclusive listings.')}
            </p>

            <Link 
              href={`/${locale}/contact`}
              className="inline-flex items-center gap-3 px-8 py-4 border transition-all duration-300 hover:bg-white/5"
              style={{ 
                borderColor: colors.primary,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <span 
                className="text-xs tracking-[0.2em] uppercase"
                style={{ color: colors.primary }}
              >
                {messages?.contactUs || 'Contact Our Team'}
              </span>
              <svg className="w-4 h-4" fill="none" stroke={colors.primary} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

/**
 * Provence Page - Pre-configured for Provence
 */
export function ProvencePage(props: Omit<RegionPageProps, 'region'>) {
  return <RegionPage {...props} region="provence" />;
}

/**
 * Mauritius Page - Pre-configured for Mauritius
 */
export function MauritiusPage(props: Omit<RegionPageProps, 'region'>) {
  return <RegionPage {...props} region="mauritius" />;
}
